import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Building2, LayoutDashboard, Users, FolderKanban, LogOut, FileText, AlertCircle, BarChart3, Settings, CreditCard, CheckSquare, Target, FileOutput, ClipboardList, Menu, X, HelpCircle, MessageSquare, FileDiff, MapPin, TrendingUp, Mail, FolderOpen, Shield, Scale, BookOpen, Wrench, Brain, Megaphone } from 'lucide-react';
import { FeedbackButtons } from '@/components/feedback/FeedbackButtons';
import { NotificationBell } from '@/components/NotificationBell';

// Logo URLs from public assets
const LOGO_WHITE = '/gantt-logo-white.png';
const LOGO_BLUE = '/gantt-logo-blue.png';

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isAdmin, canAccessAdminDashboard, canManageSMTP, canViewReports, can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Build nav items based on user permissions
  const navItems = [];
  
  // Dashboard is available to all authenticated users
  navItems.push({ icon: LayoutDashboard, label: 'Dashboard', path: '/', hash: 'overview' });
  
  // Admin-only items
  if (isAdmin()) {
    navItems.push({ icon: Users, label: 'Clients', path: '/', hash: 'clients' });
  }
  
  // Projects tab visible to ALL authenticated users (they see their assigned projects)
  navItems.push({ icon: FolderKanban, label: 'Projects', path: '/', hash: 'projects' });
  
  // Document Control - context-aware: show when inside a project
  const projectMatch = location.pathname.match(/\/projects\/([^/]+)/);
  if (projectMatch) {
    navItems.push({ icon: FolderOpen, label: 'Document Control', path: `/projects/${projectMatch[1]}/setup` });
    if (can('closeout', 'view')) {
      navItems.push({ icon: Shield, label: 'Close-Out Engine', path: `/projects/${projectMatch[1]}/closeout` });
    }
    if (can('disputes', 'view')) {
      navItems.push({ icon: Scale, label: 'Dispute Resolution', path: `/projects/${projectMatch[1]}/disputes` });
    }
    navItems.push({ icon: ClipboardList, label: 'Periodic Reports', path: `/projects/${projectMatch[1]}/periodic-reports` });
  }
  
  // Project Map - admin only
  if (isAdmin()) {
    navItems.push({ icon: MapPin, label: 'Project Map', path: '/project-map' });
    navItems.push({ icon: TrendingUp, label: 'Executive Dashboard', path: '/executive-dashboard' });
  }
  
  // Tasks - available to all authenticated users
  navItems.push({ icon: CheckSquare, label: 'Tasks', path: '/tasks' });
  navItems.push({ icon: Mail, label: 'Correspondence', path: '/correspondence' });
  
  // Help Portal - available to all users to view their tickets
  navItems.push({ icon: HelpCircle, label: 'Support Tickets', path: '/help' });
  navItems.push({ icon: BookOpen, label: 'Knowledge Base', path: '/knowledge-base' });
  
  // CRM - admin only
  if (isAdmin()) {
    navItems.push({ icon: Target, label: 'CRM', path: '/crm' });
    navItems.push({ icon: Megaphone, label: 'Marketing Flyer', path: '/marketing-flyer' });
  }
  
  // Documents - admin only
  if (isAdmin()) {
    navItems.push({ icon: FileOutput, label: 'Documents', path: '/documents' });
  }
  
  // Users tab - admin only (dedicated page)
  if (isAdmin()) {
    navItems.push({ icon: Users, label: 'User Management', path: '/admin/users' });
  }
  
  // Feedback Management - admin only
  if (isAdmin()) {
    navItems.push({ icon: MessageSquare, label: 'Feedback Mgmt', path: '/admin/feedback' });
  }
  
  // Email Template Preview - admin only
  if (isAdmin()) {
    navItems.push({ icon: Mail, label: 'Email Templates', path: '/admin/email-templates' });
  }
  
  // User Settings - available to all
  navItems.push({ icon: Settings, label: 'Settings', path: '/settings' });
  
  // Email Settings - admin only
  if (canManageSMTP()) {
    navItems.push({ icon: Mail, label: 'Email Settings', path: '/', hash: 'settings' });
  }
  
  // Company Settings - admin only
  if (isAdmin()) {
    navItems.push({ icon: Building2, label: 'Company', path: '/company-settings' });
  }
  
  // Billing - admin only
  if (isAdmin()) {
    navItems.push({ icon: CreditCard, label: 'Billing', path: '/billing' });
  }
  
  
  // Reports - admin, director, contracts_manager, contractor
  if (canViewReports()) {
    navItems.push({ icon: BarChart3, label: 'Reports', path: '/reports' });
  }

  const handleNavClick = (item) => {
    if (item.hash) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          window.location.hash = item.hash;
        }, 100);
      } else {
        window.location.hash = item.hash;
      }
    } else {
      navigate(item.path);
    }
    setMobileMenuOpen(false);
  };

  const isItemActive = (item) => {
    if (item.hash) {
      const currentHash = location.hash.replace('#', '');
      return currentHash === item.hash || (!currentHash && item.hash === 'overview');
    }
    return location.pathname === item.path;
  };

  // Navigation content (shared between mobile and desktop)
  const NavigationContent = ({ isMobile = false }) => (
    <>
      <nav className={`flex-1 ${isMobile ? 'p-2' : 'p-4'} space-y-1 overflow-y-auto`}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = isItemActive(item);
          
          return (
            <button
              key={item.label}
              onClick={() => handleNavClick(item)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all ${
                isMobile ? '' : 'hover:translate-x-1'
              } ${
                isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
              <span className="uppercase tracking-wide text-xs">{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className={`${isMobile ? 'p-2' : 'p-4'} border-t border-slate-800`}>
        <div className="mb-4 p-3 bg-slate-800 rounded-sm">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Logged in as</p>
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-slate-400 font-mono truncate">{user?.email}</p>
          <p className="text-xs uppercase tracking-wider text-slate-500 mt-2 inline-block px-2 py-1 bg-slate-700 rounded">
            {user?.role}
          </p>
        </div>
        <Button
          onClick={handleLogout}
          data-testid="logout-button"
          variant="ghost"
          className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="h-4 w-4 mr-3" strokeWidth={1.5} />
          <span className="uppercase tracking-wide text-xs">Logout</span>
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-dvh" style={{ height: '100dvh' }}>
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:flex w-64 bg-slate-900 text-white flex-col" data-testid="sidebar">
        {/* Logo section - same dark blue as sidebar */}
        <div className="p-6 border-b border-slate-800">
          <div 
            className="flex items-center justify-center cursor-pointer transition-opacity hover:opacity-80"
            onClick={() => navigate('/')}
            title="Go to Dashboard"
          >
            <img src={LOGO_WHITE} alt="Gantt" className="h-14 w-auto" />
          </div>
        </div>
        <NavigationContent />
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header - Visible only on mobile/tablet */}
        <header className="lg:hidden bg-slate-900 text-white flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-slate-800 p-2"
                  data-testid="mobile-menu-button"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-slate-900 border-slate-800">
                {/* Mobile sidebar logo */}
                <div className="p-5 border-b border-slate-800">
                  <div 
                    className="flex items-center justify-center cursor-pointer"
                    onClick={() => { setMobileMenuOpen(false); navigate('/'); }}
                  >
                    <img src={LOGO_WHITE} alt="Gantt" className="h-12 w-auto" />
                  </div>
                </div>
                <div className="flex flex-col h-[calc(100dvh-88px)] text-white">
                  {/* Scrollable nav section */}
                  <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
                    {navItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = isItemActive(item);
                      
                      return (
                        <button
                          key={item.label}
                          onClick={() => handleNavClick(item)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all ${
                            isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                          }`}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
                          <span className="uppercase tracking-wide text-xs">{item.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                  
                  {/* Fixed logout section at bottom */}
                  <div className="p-2 border-t border-slate-800 mt-auto">
                    <div className="mb-3 p-2 bg-slate-800 rounded-sm">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Logged in as</p>
                      <p className="text-sm font-medium truncate">{user?.name}</p>
                      <p className="text-xs text-slate-400 font-mono truncate">{user?.email}</p>
                    </div>
                    <Button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      data-testid="mobile-logout-button"
                      variant="ghost"
                      className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
                    >
                      <LogOut className="h-4 w-4 mr-3" strokeWidth={1.5} />
                      <span className="uppercase tracking-wide text-xs">Logout</span>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <img 
              src={LOGO_WHITE} 
              alt="Gantt" 
              className="h-10 w-auto cursor-pointer" 
              onClick={(e) => { e.stopPropagation(); navigate('/'); }}
            />
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Hide feedback on very small screens */}
            <div className="hidden sm:block">
              <FeedbackButtons />
            </div>
            <NotificationBell />
            <span className="text-xs text-slate-400 hidden sm:block">{user?.name}</span>
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-white">{user?.name?.charAt(0)?.toUpperCase()}</span>
            </div>
            {/* Mobile logout button - ALWAYS visible, never shrink */}
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-red-600 hover:text-white p-2 flex-shrink-0 bg-slate-800"
              data-testid="mobile-header-logout"
              title="Logout"
            >
              <LogOut className="h-5 w-5" strokeWidth={2} />
            </Button>
          </div>
        </header>

        {/* Desktop Header with Feedback Buttons */}
        <header className="hidden lg:flex bg-white border-b border-slate-200 px-6 py-3 items-center justify-end gap-3">
          <NotificationBell />
          <FeedbackButtons />
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <div className="p-4 md:p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
